import React from 'react';
import { List, Typography, Box, CircularProgress } from '@mui/material';
import CommandPaletteResultItem from './CommandPaletteResultItem';
import type { SearchResult, ResultCategory } from './types';

const CATEGORY_LABELS: Record<ResultCategory, string> = {
  pinned: 'Pinned Cases',
  activeToday: 'Working on Today',
  myCases: 'My Cases',
  cases: 'Cases',
  contacts: 'Contacts',
  customers: 'Customers',
  recentActivity: 'Recent Activity',
  navigation: 'Navigation',
};

interface CommandPaletteResultsProps {
  results: SearchResult[];
  activeIndex: number;
  onSelect: (index: number) => void;
  onHover: (index: number) => void;
  listRef: React.RefObject<HTMLUListElement>;
  isLoading: boolean;
  query: string;
}

const CommandPaletteResults: React.FC<CommandPaletteResultsProps> = ({
  results,
  activeIndex,
  onSelect,
  onHover,
  listRef,
  isLoading,
  query,
}) => {
  if (isLoading && results.length === 0) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress size={24} />
      </Box>
    );
  }

  if (query.length > 0 && results.length === 0 && !isLoading) {
    return (
      <Box sx={{ py: 4, textAlign: 'center' }}>
        <Typography variant="body2" color="text.secondary">
          No results found
        </Typography>
      </Box>
    );
  }

  if (results.length === 0) {
    return (
      <Box sx={{ py: 4, textAlign: 'center' }}>
        <Typography variant="body2" color="text.secondary">
          Start typing to search, or use a prefix:
        </Typography>
        <Typography variant="caption" color="text.secondary" component="div" sx={{ mt: 1 }}>
          <strong>&gt;</strong> actions &nbsp;&nbsp; <strong>#</strong> case ID &nbsp;&nbsp; <strong>@</strong> contacts &nbsp;&nbsp; <strong>!</strong> my cases
        </Typography>
      </Box>
    );
  }

  // Group results by category for headers
  const grouped: { category: ResultCategory; startIndex: number; items: SearchResult[] }[] = [];
  let currentCategory: ResultCategory | null = null;

  results.forEach((result, idx) => {
    if (result.category !== currentCategory) {
      currentCategory = result.category;
      grouped.push({ category: result.category, startIndex: idx, items: [] });
    }
    grouped[grouped.length - 1].items.push(result);
  });

  return (
    <List
      ref={listRef}
      id="command-palette-results"
      role="listbox"
      sx={{
        maxHeight: 400,
        overflowY: 'auto',
        py: 0.5,
      }}
    >
      {grouped.map((group) => (
        <React.Fragment key={group.category}>
          <Box
            role="group"
            aria-label={CATEGORY_LABELS[group.category]}
          >
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{
                px: 2,
                py: 0.5,
                display: 'block',
                fontWeight: 600,
                textTransform: 'uppercase',
                fontSize: '0.675rem',
                letterSpacing: '0.05em',
              }}
            >
              {CATEGORY_LABELS[group.category]}
            </Typography>
            {group.items.map((result, itemIdx) => {
              const globalIndex = group.startIndex + itemIdx;
              return (
                <CommandPaletteResultItem
                  key={result.id}
                  result={result}
                  index={globalIndex}
                  isActive={globalIndex === activeIndex}
                  onMouseEnter={() => onHover(globalIndex)}
                  onClick={() => onSelect(globalIndex)}
                />
              );
            })}
          </Box>
        </React.Fragment>
      ))}
    </List>
  );
};

export default CommandPaletteResults;
